import React, { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Search, SlidersHorizontal, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Clock, Play, RotateCcw, Lock, Code2, Copy, Award
} from "lucide-react";
import Editor from "@monaco-editor/react";
import apiClient from "../../../utils/apiClient";
import DataTable from "../../Common/DataTable";
import { PageHeader, AppBadge, AppButton } from "../../ui";
import { FilterModal, FilterGroup } from "../../ui/FilterModal";
import { getCurrentUser } from "../../../utils/auth";
import { formatToReadableDateTime } from "../../../utils/common";
import "../Assignments.scss";

const SESSION_STATUSES = ["", "pending", "active", "submitted", "expired"];

const sessionStyle = {
  pending: { variant: "secondary", label: "Not Started" },
  active: { variant: "info", label: "In Progress" },
  submitted: { variant: "success", label: "Submitted" },
  expired: { variant: "danger", label: "Expired" },
};

const StudentAssignmentsPage = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const [assignments, setAssignments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({ search: "", sessionStatus: "" });
  const [searchInput, setSearchInput] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/assignment-sessions/list", {
        params: {
          studentId: currentUser.studentId,
          ...filters,
          page,
          limit: pageSize,
        },
      });
      setAssignments(data.assignments || []);
      setSummary(data.summary || null);
      setTotalRecords(data.total || 0);
    } catch {
      setAssignments([]);
    }
    setLoading(false);
  }, [filters, page, pageSize, currentUser.studentId]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);
  useEffect(() => { setPage(1); }, [filters]);

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) setFilters(f => ({ ...f, search: searchInput }));
  };

  const handleStart = async (assignmentId) => {
    try {
      const res = await apiClient.post(`/assignment-sessions/start/${assignmentId}`, {
        _id: currentUser.studentId,
      });
      navigate(`/s/assignment-session/${res.data.session._id}`);
    } catch {
      console.log("Failed to start assignment");
    }
  };

  const handleRestart = async (sessionId) => {
    try {
      const res = await apiClient.post(`/assignment-sessions/${sessionId}/restart`, {});
      navigate(`/s/assignment-session/${res.data.session._id}`);
    } catch {
      console.log("Failed to restart assignment");
    }
  };

  const toggleExpand = (id) => {
    setExpandedRow(prev => prev === id ? null : id);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const activeFilterCount = filters.sessionStatus ? 1 : 0;

  const columns = [
    {
      key: "expand",
      label: "",
      width: "40px",
      render: (r) => (
        <button
          className="asd-expand-btn"
          onClick={(e) => { e.stopPropagation(); toggleExpand(r._id); }}
          disabled={r.sessionStatus !== "submitted"}
        >
          {expandedRow === r._id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ),
    },
    {
      key: "index",
      label: "#",
      width: "40px",
      render: (_, index) => (page - 1) * pageSize + index + 1,
    },
    {
      key: "title",
      label: "Assignment",
      width: "22%",
      render: (r) => (
        <div>
          <div className="fw-medium">{r.title}</div>
          <div className="sal-meta">
            {r.batch} · {r.numQuestions} Q{r.numQuestions !== 1 ? "s" : ""} · {r.timeLimit || "—"} min
          </div>
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      width: "8%",
      render: (r) => (
        <AppBadge variant={r.assignmentType === "practice" ? "info" : "secondary"}>
          {r.assignmentType === "practice" ? "Practice" : "Exam"}
        </AppBadge>
      ),
    },
    {
      key: "schedule",
      label: "Schedule",
      width: "16%",
      render: (r) => (
        <div className="sal-schedule">
          <div><span className="sal-schedule-label">Start:</span> {r.startDate ? formatToReadableDateTime(r.startDate) : "—"}</div>
          <div><span className="sal-schedule-label">End:</span> {r.endDate ? formatToReadableDateTime(r.endDate) : "—"}</div>
        </div>
      ),
    },
    {
      key: "progress",
      label: "Progress",
      width: "10%",
      render: (r) => {
        const s = sessionStyle[r.sessionStatus] || sessionStyle.pending;
        return <AppBadge variant={s.variant}>{s.label}</AppBadge>;
      },
    },
    {
      key: "score",
      label: "Score",
      width: "12%",
      render: (r) => {
        if (r.sessionStatus !== "submitted" || r.score === null) return <span className="text-muted">—</span>;
        const scoreVal = r.scoreOutOf100 ?? (r.totalTests > 0 ? Math.round((r.totalPassed / r.totalTests) * 100) : 0);
        const allPassed = scoreVal === 100;
        return (
          <div className="asd-score-cell">
            <span className={`asd-score-100 ${allPassed ? "asd-score-100--perfect" : scoreVal >= 50 ? "asd-score-100--good" : "asd-score-100--low"}`}>
              {scoreVal}<span className="asd-score-100-max">/100</span>
            </span>
            <span className="asd-score-tc-sub">{r.totalPassed}/{r.totalTests} test cases</span>
          </div>
        );
      },
    },
    {
      key: "testCases",
      label: "Questions",
      width: "10%",
      render: (r) => {
        if (r.sessionStatus !== "submitted" || !r.totalQuestions) return <span className="text-muted">—</span>;
        const allPassed = r.score === r.totalQuestions;
        return (
          <span className={`asd-score ${allPassed ? "asd-score--pass" : "asd-score--partial"}`}>
            {r.score}/{r.totalQuestions} passed
          </span>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      width: "10%",
      render: (r) => {
        if (r.sessionStatus === "submitted") {
          if (r.assignmentType === "practice") {
            return (
              <button className="sal-restart-btn" onClick={(e) => { e.stopPropagation(); handleRestart(r.sessionId); }}>
                <RotateCcw size={13} /> Restart
              </button>
            );
          }
          return <span className="sal-completed"><CheckCircle size={13} /> Done</span>;
        }
        if (r.sessionStatus === "expired") {
          return <span className="sal-locked"><Lock size={13} /> Locked</span>;
        }
        return (
          <AppButton size="sm" onClick={(e) => { e.stopPropagation(); handleStart(r._id); }}>
            <Play size={13} /> {r.sessionStatus === "active" ? "Continue" : "Start"}
          </AppButton>
        );
      },
    },
  ];

  /* ── Expanded row: show submitted code + test results ── */
  const renderExpandedRow = (r) => {
    if (expandedRow !== r._id || !r.answers?.length) return null;

    return (
      <tr key={`exp-${r._id}`} className="asd-expanded-row">
        <td colSpan={columns.length}>
          <div className="asd-answers">
            {r.answers.map((ans, qi) => {
              const lang = Object.keys(ans.code || {})[0] || "—";
              const code = ans.code?.[lang] || "";
              const visible = (ans.results || []).filter(t => !t.isHidden);
              const hidden = (ans.results || []).filter(t => t.isHidden);
              const hiddenPassed = hidden.filter(t => t.passed).length;

              return (
                <div key={qi} className="asd-answer-card">
                  <div className="asd-answer-header">
                    <span className="asd-answer-q">Q{qi + 1}: {ans.question}</span>
                    <AppBadge variant={ans.status === "passed" ? "success" : ans.status === "failed" ? "danger" : "secondary"}>
                      {ans.status}
                    </AppBadge>
                    <span className={`asd-answer-score-100 ${ans.total > 0 && ans.passedCount === ans.total ? "asd-answer-score-100--perfect" : ""}`}>
                      {ans.total > 0 ? Math.round((ans.passedCount / ans.total) * 100) : 0}/100
                    </span>
                    <span className="asd-answer-tc">
                      <CheckCircle size={12} /> {ans.passedCount}/{ans.total} test cases
                    </span>
                    {lang !== "—" && (
                      <span className="asd-answer-lang">
                        <Code2 size={12} /> {lang}
                      </span>
                    )}
                  </div>

                  {/* Test case results */}
                  {visible.length > 0 && (
                    <div className="asd-tc-grid">
                      {visible.map((tc, ti) => (
                        <div key={ti} className={`asd-tc-item ${tc.passed ? "asd-tc-item--pass" : "asd-tc-item--fail"}`}>
                          <div className="asd-tc-status">
                            {tc.passed ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>Case {ti + 1}</span>
                          </div>
                          <div className="asd-tc-data">
                            <div><span className="asd-tc-label">Input</span><code>{tc.input || "(empty)"}</code></div>
                            <div><span className="asd-tc-label">Expected</span><code>{tc.expected}</code></div>
                            <div><span className="asd-tc-label">Output</span><code>{tc.output || "—"}</code></div>
                          </div>
                        </div>
                      ))}
                      {hidden.length > 0 && (
                        <div className={`asd-tc-item ${hiddenPassed === hidden.length ? "asd-tc-item--pass" : "asd-tc-item--fail"} asd-tc-item--hidden`}>
                          <div className="asd-tc-status">
                            {hiddenPassed === hidden.length ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>{hiddenPassed}/{hidden.length} hidden test case{hidden.length !== 1 ? "s" : ""} passed</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submitted code */}
                  {code && (
                    <div className="asd-code-block">
                      <div className="asd-code-header">
                        <span>Your Code ({lang})</span>
                        <button className="asd-copy-btn" onClick={() => handleCopyCode(code)}>
                          <Copy size={12} />
                          {copiedCode === code ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <Editor
                        height={Math.min(300, Math.max(100, code.split("\n").length * 19))}
                        language={lang}
                        value={code}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 13,
                          lineNumbers: "on",
                          folding: false,
                          contextmenu: false,
                          renderLineHighlight: "none",
                          padding: { top: 8 },
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    );
  };

  if (loading && assignments.length === 0) {
    return <div className="d-flex justify-content-center my-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="sal-page">
      <PageHeader
        icon={ClipboardList}
        title="My Assignments"
        description="View your coding assignments, scores, and submitted code"
      />

      {/* Summary Stats */}
      {summary && (
        <div className="sal-stats">
          <div className="sal-stat-card">
            <ClipboardList size={18} className="asd-stat-icon" />
            <div>
              <div className="asd-stat-value">{summary.totalAssignments}</div>
              <div className="asd-stat-label">Total</div>
            </div>
          </div>
          <div className="sal-stat-card sal-stat-card--success">
            <CheckCircle size={18} className="asd-stat-icon" />
            <div>
              <div className="asd-stat-value">{summary.completed}</div>
              <div className="asd-stat-label">Completed</div>
            </div>
          </div>
          <div className="sal-stat-card sal-stat-card--warning">
            <Clock size={18} className="asd-stat-icon" />
            <div>
              <div className="asd-stat-value">{summary.inProgress}</div>
              <div className="asd-stat-label">In Progress</div>
            </div>
          </div>
          <div className="sal-stat-card sal-stat-card--info">
            <Award size={18} className="asd-stat-icon" />
            <div>
              <div className="asd-stat-value">{summary.pending}</div>
              <div className="asd-stat-label">Pending</div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="asl-toolbar">
        <div className="asl-search">
          <Search size={16} className="asl-search-icon" />
          <input
            type="text"
            className="asl-search-input"
            placeholder="Search assignments..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="asl-toolbar-actions">
          <select
            className="asl-filter-select d-none d-md-block"
            value={filters.sessionStatus}
            onChange={(e) => setFilters(f => ({ ...f, sessionStatus: e.target.value }))}
          >
            <option value="">All Progress</option>
            {SESSION_STATUSES.filter(Boolean).map(s => (
              <option key={s} value={s}>{(sessionStyle[s]?.label) || s}</option>
            ))}
          </select>

          <button
            className={`asl-filter-btn d-md-none${activeFilterCount > 0 ? " asl-filter-btn--active" : ""}`}
            onClick={() => setShowFilterModal(true)}
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="asl-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onReset={() => setFilters(f => ({ ...f, sessionStatus: "" }))}
        activeCount={activeFilterCount}
      >
        <FilterGroup label="Progress">
          <select
            className="asl-filter-select"
            value={filters.sessionStatus}
            onChange={(e) => setFilters(f => ({ ...f, sessionStatus: e.target.value }))}
          >
            <option value="">All Progress</option>
            {SESSION_STATUSES.filter(Boolean).map(s => (
              <option key={s} value={s}>{(sessionStyle[s]?.label) || s}</option>
            ))}
          </select>
        </FilterGroup>
      </FilterModal>

      <DataTable
        data={assignments}
        columns={columns}
        loading={loading}
        hoverable
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage,
        }}
        emptyMessage="No assignments available"
        renderExpandedRow={renderExpandedRow}
      />
    </div>
  );
};

export default StudentAssignmentsPage;
